/* Derived view-model for the platform route.
 * GENERATED from the original artifact's vals_platform() by
 * tools/convert-logic.py; edits since generation are tracked in git. */
import { CLUSTERS, FOOTER_COLS, SECTIONS } from '@/data/wft';
import type { ClusterId } from '@/data/wft';

export function platformVals() {
  const clusterIds: ClusterId[] = ['tape', 'context', 'engine', 'desk', 'system'];

  return {
    footerCols: FOOTER_COLS,
    coverage: clusterIds.map((c) => {
      const secs = SECTIONS.filter((x) => x.cluster === c);
      const panels = secs.flatMap((x) => x.panels);
      const bound = panels.filter((p) => p.sources.length > 0).length;
      return {
        label: CLUSTERS[c].label,
        bound: String(bound),
        total: String(panels.length),
        meta: secs.length + ' sections · ' + (panels.length - bound) + ' awaiting a connector',
        cells: panels.map((p) => ({
          title:
            p.title +
            (p.sources.length
              ? ' — ' + p.sources.join(' · ')
              : ' — ' + (p.wireIn || 'no connector')),
          color: p.sources.length ? 'var(--wft-engine-dim)' : 'var(--wft-accent)',
        })),
      };
    }),
    sectionList: SECTIONS.map((x) => ({
      mnemonic: x.mnemonic,
      title: x.title,
      purpose: x.purpose,
      meta:
        CLUSTERS[x.cluster].label +
        ' · g' +
        x.leader +
        ' · ' +
        x.panels.length +
        ' panels' +
        (x.live ? '' : ' · no connector at M1'),
      panels: x.panels.map((p) => ({
        title: p.title,
        kind: p.kind,
        feed: p.sources.length ? p.sources.join(' · ') : 'wire in — ' + (p.wireIn || 'tbd'),
        feedColor: p.sources.length ? 'var(--wft-ink-2)' : 'var(--wft-accent)',
      })),
    })),
    anatomy: [
      {
        part: 'Header',
        body: 'Index for the 1–9 focus shortcut, the panel title, and one dot per bound source showing the worst state among them.',
      },
      {
        part: 'Banner',
        body: 'Appears only when a feed is partial or down, and says which of the two it is in words.',
      },
      {
        part: 'Body',
        body: 'The widget, or the hatch. Never both, and never a shimmer standing in for either.',
      },
      {
        part: 'Bindings',
        body: 'Declared per panel. Staleness is derived from the clock so a panel demotes itself without a re-render.',
      },
      {
        part: 'Export',
        body: 'Copy and export are disabled on panels fed only by non-redistributable sources.',
      },
    ],
    layouts: [
      {
        name: 'Cockpit · desk',
        meta: '3 columns',
        cols: 'repeat(3, 1fr)',
        cells: [
          { area: 'tape', span: 'span 3', h: '30px' },
          { area: 'heat', span: 'span 2', h: '58px' },
          { area: 'calls', span: 'span 1', h: '58px' },
          { area: 'wires', span: 'span 2', h: '48px' },
          { area: 'clocks', span: 'span 1', h: '22px' },
          { area: 'sources', span: 'span 1', h: '22px' },
        ],
      },
      {
        name: 'Cockpit · laptop',
        meta: '2 columns',
        cols: 'repeat(2, 1fr)',
        cells: [
          { area: 'tape', span: 'span 2', h: '30px' },
          { area: 'heat', span: 'span 1', h: '48px' },
          { area: 'calls', span: 'span 1', h: '48px' },
          { area: 'wires', span: 'span 2', h: '44px' },
          { area: 'clocks', span: 'span 1', h: '26px' },
          { area: 'sources', span: 'span 1', h: '26px' },
        ],
      },
    ],
    principles: [
      {
        title: 'One registry',
        body: 'Rail, command bar, leader map and router all read the same declaration. Adding a section is one entry, not five edits in five files.',
      },
      {
        title: 'Desktop-only is declared',
        body: 'Correlation matrices, world maps and linkage graphs mark themselves desktop-only. Below their minimum width they say so rather than misrepresenting the data.',
      },
      {
        title: 'The page never scrolls',
        body: 'The frame is the viewport; panels scroll inside it. Height uses dynamic viewport units because mobile browser chrome makes the static ones lie.',
      },
      {
        title: 'Two themes, one product',
        body: 'Terminal is the dark theme the product is designed in; Canon is the light remap of the same token names. A third theme is one token block and zero component edits.',
      },
    ],
  };
}

export type PlatformVals = ReturnType<typeof platformVals>;
